import { forwardRef, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { InlineMessage } from "chameleon/lib/components";
import { CreditCardNetwork } from "chameleon/lib/components/display-cards/display-cards";
import { inputWrapperClass } from "chameleon/lib/components/input/input.css";

import { FieldFrameJSX } from "checkout-experience/components/field-frame/wrapper";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { FIELD_IFRAME_URL, UI_ROOT } from "checkout-experience/utils/config";

import { isValueEncrypted } from "encrypt-value/tools";

import {
  CustomEventMessage,
  ExternalFieldFrameProps,
} from "field-frames/src/frame/types";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

interface ExtendedExternalFieldFrameProps
  extends Omit<ExternalFieldFrameProps, "type" | "encryptionKey"> {
  supportedPaymentTypes: CreditCardNetwork[];
  errorMessage: string;
  errorMessageKey?: string;
  value?: string;
}

export const MapFieldFrame = forwardRef<
  HTMLDivElement,
  ExtendedExternalFieldFrameProps
>((props: ExtendedExternalFieldFrameProps, forwardedRef) => {
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { merchantSettings, target } = useChexContext();
  const { setError, clearErrors } = useFormContext();
  const errorRef = useRef<Array<string>>([]);
  const captionID = `${props.id}-${props.errorMessageKey}-caption`;
  const [hasBlurred, setHasBlurred] = useState(false);

  const updateErrorStatus = () => {
    if (errorRef.current.length > 0) {
      setError(`${paymentOption?.formId}.${props.id}`, {
        type: errorRef.current[0],
        message: errorRef.current[0],
      });
    } else {
      clearErrors(`${paymentOption?.formId}.${props.id}`);
    }
  };

  const onError = (errors: Array<string>) => {
    errorRef.current = errors;
    updateErrorStatus();
  };

  const onBlur = () => {
    setHasBlurred(() => true);
    // Don't announce blur because this overwrites the Error codes, instead set them manually
    updateErrorStatus();
  };

  // @ts-ignore // Custom event "Action"
  window.addEventListener("action", (event: CustomEventMessage) => {
    if (
      event.detail.message === "validate"
      // event.detail.payload?.source === props.id
    ) {
      setHasBlurred(true);
    }
  });

  if (!merchantSettings?.encryptionKey) {
    return <></>;
  }

  // remap id to type
  let type: ExternalFieldFrameProps["type"] =
    props.id as ExternalFieldFrameProps["type"];

  // Remap default expiration to expiration date
  if (props.id === "expiration") {
    type = "expirationDate";
  }

  const fieldIframeUrl = getIsFeatureEnabled(target ?? "", "field-frame-bundle")
    ? "/field-inline.html"
    : FIELD_IFRAME_URL;
  return (
    <div
      data-testid={`${props.id}-fieldframe`}
      className={`${inputWrapperClass}`}
    >
      <FieldFrameJSX
        ref={forwardedRef}
        id={props.id}
        title={props.title}
        onBlur={onBlur}
        onChange={props.onChange}
        onError={onError}
        placeholder={props.placeholder}
        ariaLabel={props.ariaLabel}
        encryptionKey={merchantSettings?.encryptionKey}
        cardTypes={props.supportedPaymentTypes}
        type={type}
        isReady={props.isReady}
        isLoading={props.isLoading}
        translatedValues={props.translatedValues}
        uiRoot={UI_ROOT}
        fieldIFrameUrl={fieldIframeUrl}
        showCheckmark={props.showCheckmark}
        showSpinner={props.showSpinner}
        fetchingCardType={props.fetchingCardType}
        selectedPaymentType={props.selectedPaymentType}
        featuresEnabled={props.featuresEnabled}
        maxLength={props.maxLength}
        isEmptyLabel={props.isEmptyLabel}
        initValue={isValueEncrypted(props.value) ? undefined : props.value}
      />
      {props.errorMessage && hasBlurred ? (
        <InlineMessage
          id={captionID}
          message={props.errorMessage}
          type={"error"}
        />
      ) : props.captionMessage ? (
        <InlineMessage
          id={`${props.id}-caption`}
          message={props.captionMessage}
        />
      ) : undefined}
    </div>
  );
});

MapFieldFrame.displayName = "MapFieldFrame";
